import { Component, OnInit } from '@angular/core';
import { ModalService } from 'npx-family-happy-common';
import { User } from 'src/app/shared/models/user.model';
import { UserManagementService } from '../user-management-service/user-management.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { B2bCompanySelectComponent } from 'src/app/shared/components/b2b-company-select/b2b-company-select.component';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, B2bCompanySelectComponent],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
})
export class UserDetailsComponent implements OnInit {
  showModal: boolean = false;
  user: any;
  step: number = 0;
  form!: FormGroup;
  newAddressForm: FormGroup = new FormGroup({
    id: new FormControl(''),
    active: new FormControl(false),
    isDefault: new FormControl(false),
    street: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    province: new FormControl(''),
    postalCode: new FormControl(''),
    zone: new FormControl(''),
    latidute: new FormControl(0),
    longitude: new FormControl(0),
  });
  newDependantForm: FormGroup = new FormGroup({
    id: new FormControl(''),
    name: new FormControl('', [Validators.required]),
    handicap: new FormControl(false),
    birthDate: new FormControl('', [Validators.required]),
    gender: new FormControl('', [Validators.required]),
    type: new FormControl('', [Validators.required]),
  });

  CAREGIVER_ROLES = [
    'BABYSITTER',
    'PETSITTER',
    'NURSE',
    'COLF',
    'BABYSITTER_PETSITTER',
    'BABYSITTER_NURSE',
    'BABYSITTER_COLF',
    'PETSITTER_NURSE',
    'PETSITTER_COLF',
    'NURSE_COLF',
    'BABYSITTER_PETSITTER_NURSE',
    'BABYSITTER_PETSITTER_COLF',
    'BABYSITTER_NURSE_COLF',
    'PETSITTER_NURSE_COLF',
    'BABYSITTER_PETSITTER_NURSE_COLF',
  ];

  // Nuove proprietà per gestire l'espansione
  expandedAddressIndex: number | null = null;
  expandedDependantIndex: number | null = null;

  ngOnInit(): void {
    // init forms
    this.initFormGroup();

    this.form.valueChanges.subscribe((value) => {});

    // get user
    const userId = this.route.snapshot.paramMap.get('uid');
    if (userId) {
      this.management.getSingleUser(userId).subscribe({
        next: (data: User) => {
          this.user = data;
          this.populateForm();
          if (this.user.addresses && this.user.addresses.length > 0) {
            this.initAddressesArray(this.user.addresses);
          }
          if (this.user.dependants && this.user.dependants.length > 0) {
            this.initDependantsArray(this.user.dependants);
          }
        },
        error: (err) => {},
      });
    } else {
      this.router.navigate(['/user-management']);
    }
  }

  constructor(
    private management: UserManagementService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: ModalService
  ) {}

  // Metodi per gestire gli indirizzi
  getAddressesControls() {
    return (this.form.get('addresses') as FormArray).controls;
  }
  toggleAddressEdit(index: number) {
    this.expandedAddressIndex =
      this.expandedAddressIndex === index ? null : index;
  }

  saveAddress(index: number) {
    const addressesFormArray = this.form.get('addresses') as FormArray;
    const addressFormGroup = addressesFormArray.at(index) as FormGroup;

    if (addressFormGroup.valid) {
      const addressData = {
        active: addressFormGroup.get('active')?.value,
        street: addressFormGroup.get('street')?.value,
        city: addressFormGroup.get('city')?.value,
        province: addressFormGroup.get('province')?.value,
        postalCode: addressFormGroup.get('postalCode')?.value,
        zone: addressFormGroup.get('zone')?.value,
        latitude: addressFormGroup.get('latitude')?.value || 0,
        longitude: addressFormGroup.get('longitude')?.value || 0,
        default: addressFormGroup.get('isDefault')?.value,
      };

      const addressId = addressFormGroup.get('id')?.value;

      if (addressId) {
        this.management.updateUserAddress(addressId, addressData).subscribe({
          next: (response) => {
            this.expandedAddressIndex = null;
            this.modalService.showSuccessMessage(
              'Indirizzo aggiornato con successo!'
            );
          },
          error: (error) => {
            this.modalService.showErrorMessage(
              "Errore durante l'aggiornamento dell'indirizzo"
            );
          },
        });
      }
    }
  }
  saveNewAddress() {
    const addressesFormArray = this.form.get('addresses') as FormArray;
    if (this.newAddressForm.valid) {
      const addressData = {
        userUID: this.user.uid,
        active: this.newAddressForm.get('active')?.value,
        street: this.newAddressForm.get('street')?.value,
        city: this.newAddressForm.get('city')?.value,
        province: this.newAddressForm.get('province')?.value,
        postalCode: this.newAddressForm.get('postalCode')?.value,
        zone: this.newAddressForm.get('zone')?.value,
        latitude: this.newAddressForm.get('latitude')?.value || 0,
        longitude: this.newAddressForm.get('longitude')?.value || 0,
        default: this.newAddressForm.get('isDefault')?.value,
      };
      this.management.createUserAddress(addressData).subscribe({
        next: (response) => {
          this.expandedAddressIndex = null;
          this.modalService.showSuccessMessage(
            'Nuovo indirizzo creato con successo!'
          );

          const responseData = response as any;

          const newAddressGroup = new FormGroup({
            id: new FormControl(responseData.id || ''),
            active: new FormControl(responseData.active || false),
            isDefault: new FormControl(responseData.isDefault || false),
            street: new FormControl(responseData.street || ''),
            city: new FormControl(responseData.city || ''),
            province: new FormControl(responseData.province || ''),
            postalCode: new FormControl(responseData.postalCode || ''),
            zone: new FormControl(responseData.zone || ''),
            latitude: new FormControl(responseData.latitude || 0),
            longitude: new FormControl(responseData.longitude || 0),
          });

          addressesFormArray.push(newAddressGroup);
          this.newAddressForm.reset();
        },
        error: (error) => {
          this.modalService.showErrorMessage(
            'Errore durante la creazione del nuovo indirizzo'
          );
        },
      });
    } else {
      this.modalService.showErrorMessage(
        'Errore durante la creazione del nuovo indirizzo, il form non è valido'
      );
    }
  }
  cancelAddressEdit(index: number) {
    this.expandedAddressIndex = null;
    if (index === -1) {
      this.newAddressForm.reset();
    }
  }
  deleteAddress(index: number) {
    this.expandedAddressIndex = null;
    const addressesFormArray = this.form.get('addresses') as FormArray;
    this.management
      .deleteUserAddress(addressesFormArray.at(index).get('id')?.value)
      .subscribe({
        next: (response) => {
          addressesFormArray.removeAt(index);
          this.modalService.showSuccessMessage(
            'Indirizzo eliminato con successo!'
          );
        },
        error: (error) => {
          this.modalService.showErrorMessage(
            "Errore durante l'eliminazione dell'indirizzo"
          );
        },
      });
  }
  // funzione per aprire il form di nuovo indirizzo
  newAddress() {
    this.expandedAddressIndex = -1;
  }

  initAutocomplete(
    inputElement: HTMLInputElement,
    addressFormGroup: AbstractControl
  ) {
    const loader = new Loader({
      apiKey: environment.googleMapsAPIKey,
      version: 'weekly',
      libraries: ['places'],
      language: 'it-IT',
    });

    loader.load().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(inputElement, {
        types: ['address'],
        componentRestrictions: { country: 'it' },
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.address_components) {
          (addressFormGroup as FormGroup).patchValue({
            street: inputElement.value,
            city:
              place.address_components.find((comp) =>
                comp.types.includes('locality')
              )?.long_name || '',
            province:
              place.address_components.find((comp) =>
                comp.types.includes('administrative_area_level_2')
              )?.short_name || '',
            postalCode:
              place.address_components.find((comp) =>
                comp.types.includes('postal_code')
              )?.long_name || '',
            zone:
              place.address_components.find((comp) =>
                comp.types.includes('sublocality')
              )?.long_name || '',
          });
        }
      });
    });
  }

  // funzione di salvataggio generale del form
  saveForm() {
    const b2bCompanyValue = this.form.value.b2bCompany?.id || null;

    const userData = {
      uid: this.form.value.uid,
      name: this.form.value.name,
      surname: this.form.value.surname,
      email: this.form.value.email,
      phone: this.form.value.phone,
      role: this.form.value.role,
      photoURL: this.form.value.photoURL,
    };

    this.management.updateUser(userData).subscribe({
      next: (response) => {
        this.modalService.showSuccessMessage('Utente aggiornato con successo!');
      },
      error: (error) => {
        this.modalService.showErrorMessage(
          "errore durante l'aggiornamento dell'utente"
        );
      },
    });

    this.management
      .updateB2bCompany(this.user.workInfo.id, b2bCompanyValue)
      .subscribe({
        next: (response) => {
          this.modalService.showSuccessMessage(
            'B2B Company aggiornata con successo!'
          );
        },
        error: (error) => {
          this.modalService.showErrorMessage(
            "Errore durante l'aggiornamento dell'utente"
          );
        },
      });
  }

  // Metodi per gestire i dependants
  getDependantsControls() {
    return (this.form.get('dependants') as FormArray).controls;
  }
  toggleDependantEdit(index: number) {
    this.expandedDependantIndex =
      this.expandedDependantIndex === index ? null : index;
  }
  saveDependant(index: number) {
    const dependantsFormArray = this.form.get('dependants') as FormArray;
    const dependantFormGroup = dependantsFormArray.at(index) as FormGroup;

    if (dependantFormGroup.valid) {
      const dependantData = {
        userUID: this.user.uid,
        name: dependantFormGroup.get('name')?.value,
        handicap: dependantFormGroup.get('handicap')?.value,
        birthDate: new Date(
          dependantFormGroup.get('birthDate')?.value
        ).toISOString(),
        gender: dependantFormGroup.get('gender')?.value,
        type: dependantFormGroup.get('type')?.value,
      };

      const dependantId = dependantFormGroup.get('id')?.value;

      if (dependantId) {
        this.management
          .updateUserDependant(dependantId, dependantData)
          .subscribe({
            next: (response) => {
              this.expandedDependantIndex = null;
              this.modalService.showSuccessMessage(
                'Persone a carico aggiornato con successo!'
              );
            },
            error: (error) => {
              this.modalService.showErrorMessage(
                "Errore durante l'aggiornamento delle persone a carico"
              );
            },
          });
      }
    } else {
      this.modalService.showErrorMessage(
        "Errore durante l'aggiornamento delle persone a carico, il form non è valido"
      );
    }
  }
  saveNewDependant() {
    const dependantsFormArray = this.form.get('dependants') as FormArray;
    if (this.newDependantForm.valid) {
      const dependantData = {
        userUID: this.user.uid,
        name: this.newDependantForm.get('name')?.value,
        handicap: this.newDependantForm.get('handicap')?.value,
        birthDate: new Date(
          this.newDependantForm.get('birthDate')?.value
        ).toISOString(),
        gender: this.newDependantForm.get('gender')?.value,
        type: this.newDependantForm.get('type')?.value,
      };
      this.management.createUserDependant(dependantData).subscribe({
        next: (response) => {
          this.expandedDependantIndex = null;
          this.modalService.showSuccessMessage(
            'Nuovo a carico creato con successo!'
          );
          const responseData = response as any;
          const newDependantGroup = new FormGroup({
            id: new FormControl(responseData.id || ''),
            name: new FormControl(responseData.name || ''),
            handicap: new FormControl(responseData.handicap || false),
            birthDate: new FormControl(
              responseData.birthDate ? responseData.birthDate.split('T')[0] : ''
            ),

            gender: new FormControl(responseData.gender || ''),
            type: new FormControl(responseData.type || ''),
          });
          dependantsFormArray.push(newDependantGroup);
          this.newDependantForm.reset();
        },
        error: (error) => {
          this.modalService.showErrorMessage(
            'Errore durante la creazione del nuovo a carico'
          );
        },
      });
    } else {
      this.modalService.showErrorMessage(
        'Errore durante la creazione del nuovo a carico, il form non é valido'
      );
    }
  }
  cancelDependantEdit(index: number) {
    // Ripristina i valori originali se necessario
    this.expandedDependantIndex = null;
  }
  deleteDependant(index: number) {
    this.expandedDependantIndex = null;
    const dependantsFormArray = this.form.get('dependants') as FormArray;
    this.management
      .deleteUserDependant(dependantsFormArray.at(index).get('id')?.value)
      .subscribe({
        next: (response) => {
          dependantsFormArray.removeAt(index);
          this.modalService.showSuccessMessage(
            'A carico eliminato con successo!'
          );
        },
        error: (error) => {
          this.modalService.showErrorMessage(
            "Errore durante l'eliminazione dell'a carico"
          );
        },
      });
  }
  // funzione per aprire il form di nuovo a carico
  newDependant() {
    this.expandedDependantIndex = -1;
  }

  // Metodo per cambiare tab
  changeTab(index: number) {
    this.step = index;
    this.expandedAddressIndex = null;
    this.expandedDependantIndex = null;
  }

  // Metodo per tornare alla lista utenti
  goBack() {
    this.router.navigate(['/users']);
  }

  populateForm() {
    if (this.user) {
      this.form.patchValue({
        name: this.user.name || '',
        surname: this.user.surname || '',
        email: this.user.email || '',
        phone: this.user.phone || '',
        role: this.user.role || '',
        uid: this.user.uid || '',
        photoURL: this.user.photoURL || '',
        b2bCompany: this.user.workInfo ? this.user.workInfo.b2bCompany : null,
      });
    }
  }

  initFormGroup() {
    this.form = new FormGroup({
      name: new FormControl(''),
      surname: new FormControl(''),
      email: new FormControl(''),
      phone: new FormControl(''),
      role: new FormControl(''),
      uid: new FormControl(''),
      photoURL: new FormControl(''),
      addresses: new FormArray([]),
      dependants: new FormArray([]),
      b2bCompany: new FormControl(''),
    });
  }

  initAddressesArray(addressesArray: any[]) {
    const addressesFormArray = this.form.get('addresses') as FormArray;

    // Pulisci completamente l'array
    while (addressesFormArray.length !== 0) {
      addressesFormArray.removeAt(0);
    }

    // Aggiungi i nuovi elementi
    addressesArray.forEach((address: any) => {
      addressesFormArray.push(
        new FormGroup({
          id: new FormControl(address.id || ''),
          active: new FormControl(
            address.active !== undefined ? address.active : false
          ),
          isDefault: new FormControl(
            address.isDefault !== undefined ? address.isDefault : false
          ),
          street: new FormControl(address.street || ''),
          city: new FormControl(address.city || ''),
          province: new FormControl(address.province || ''),
          postalCode: new FormControl(address.postalCode || ''),
          zone: new FormControl(address.zone || ''),
        })
      );
    });
  }

  initDependantsArray(dependantsArray: any[]) {
    const dependantsFormArray = this.form.get('dependants') as FormArray;

    while (dependantsFormArray.length !== 0) {
      dependantsFormArray.removeAt(0);
    }

    // Aggiungi i nuovi elementi
    dependantsArray.forEach((dependant: any) => {
      let birthDateForInput = '';
      if (dependant.birthDate) {
        try {
          birthDateForInput = dependant.birthDate.split('T')[0];
        } catch (error) {
          birthDateForInput = '';
        }
      }

      dependantsFormArray.push(
        new FormGroup({
          id: new FormControl(dependant.id || ''),
          name: new FormControl(dependant.name || ''),
          handicap: new FormControl(
            dependant.handicap !== undefined ? dependant.handicap : false
          ),
          birthDate: new FormControl(birthDateForInput), // ✅ USA LA DATA CONVERTITA
          gender: new FormControl(dependant.gender || ''),
          type: new FormControl(dependant.type || ''),
        })
      );
    });
  }

  onImageError(event: Event) {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}
